import { useState } from 'react';
import { tagColor } from '../utils/tagColor.js';

const COLOR = {
  blue:    { border: 'border-slate-200', accent: 'bg-indigo-600',  tag: 'bg-indigo-50 text-indigo-700 border-indigo-100'     },
  teal:    { border: 'border-slate-200', accent: 'bg-teal-600',    tag: 'bg-teal-50 text-teal-700 border-teal-100'           },
  green:   { border: 'border-slate-200', accent: 'bg-emerald-600', tag: 'bg-emerald-50 text-emerald-700 border-emerald-100'   },
  yellow:  { border: 'border-slate-200', accent: 'bg-amber-500',   tag: 'bg-amber-50 text-amber-700 border-amber-100'         },
  red:     { border: 'border-slate-200', accent: 'bg-red-600',     tag: 'bg-red-50 text-red-700 border-red-100'               },
  pink:    { border: 'border-slate-200', accent: 'bg-pink-600',    tag: 'bg-pink-50 text-pink-700 border-pink-100'             },
  purple:  { border: 'border-slate-200', accent: 'bg-violet-600',  tag: 'bg-violet-50 text-violet-700 border-violet-100'       },
  orange:  { border: 'border-slate-200', accent: 'bg-orange-500',  tag: 'bg-orange-50 text-orange-700 border-orange-100'       },
  cyan:    { border: 'border-slate-200', accent: 'bg-sky-500',     tag: 'bg-sky-50 text-sky-700 border-sky-100'               },
  lime:    { border: 'border-slate-200', accent: 'bg-lime-600',    tag: 'bg-lime-50 text-lime-700 border-lime-100'             },
  rose:    { border: 'border-slate-200', accent: 'bg-rose-600',    tag: 'bg-rose-50 text-rose-700 border-rose-100'             },
  fuchsia: { border: 'border-slate-200', accent: 'bg-fuchsia-600', tag: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100'   },
};

const SOURCE_ICONS = {
  link: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  ),
  pdf: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
  text: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
};

function CopyTextButton({ content }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try { await navigator.clipboard.writeText(content); }
    catch {
      const el = document.createElement('textarea');
      el.value = content;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handleCopy}
      className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all cursor-pointer ${
        copied
          ? 'bg-green-50 text-green-700 border border-green-200'
          : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50'
      }`}
    >
      {copied ? (
        <>
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          コピー済み
        </>
      ) : (
        <>
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          コピー
        </>
      )}
    </button>
  );
}

export default function NotebookSampleCard({ item, onSelect }) {
  const c = COLOR[tagColor(item.tag) || item.color] || COLOR.blue;
  const sources = Array.isArray(item.sources) ? item.sources : [];

  return (
    <div
      onClick={onSelect}
      className={`rounded-2xl border ${c.border} bg-white shadow-card hover:shadow-card-hover transition-all overflow-hidden flex flex-col cursor-pointer`}
    >
      {/* header */}
      <div className={`${c.accent} px-4 py-3.5 flex items-start gap-2`}>
        {item.icon && <span className="text-lg leading-none mt-0.5 flex-shrink-0">{item.icon}</span>}
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-bold text-base leading-snug">{item.name}</h3>
          {item.tag && (
            <span className="inline-block mt-1.5 text-xs px-2 py-0.5 rounded-full bg-white/20 text-white/90">
              {item.tag}
            </span>
          )}
        </div>
      </div>

      {/* body */}
      <div className="p-4 flex-1 flex flex-col gap-2">
        {item.desc && (
          <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
        )}
        {sources.length > 0 && (
          <p className="text-xs text-slate-400 italic">クリックして詳細を表示</p>
        )}
      </div>
    </div>
  );
}
